import { Card, CardContent, CardFooter, CardHeader } from './ui/card';
import { Badge } from './ui/badge';
import { formatTimeAgo, remainingQuestionTime } from '../util/DateUtil';
import { ClockIcon, InfoIcon, UserIcon } from './ui/icons';
import { Button } from './ui/button';
import LoginRequireModal from './modal/LoginRequireModal';
import { useAuth } from '../context/AuthContext';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QuestionV2 } from '../types/Question.type';
import { useCategories } from '../context/CategoryContext';

interface QuestionCardProps {
  question: QuestionV2;
}

const QuestionCardV2 = ({ question }: QuestionCardProps) => {
  /**
   * Hooks
   */
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { categories } = useCategories();
  const categoryDetails = categories.find(cat => cat.id === question.categoryId);

  /**
   * States
   */
  const [isOpenLoginModal, setIsOpenLoginModal] = useState<boolean>(false);

  /**
   * Handlers
   */
  /* 카드 클릭 handler */
  const handleCardClick = (questionId: string) => {
    // 회원전용 설문인 경우, 비회원이 클릭했을 때, 로그인 필요 팝업 열기
    if (question.memberOnly && !isAuthenticated) {
      setIsOpenLoginModal(true);
      return;
    }
    navigate(`/question/info/${questionId}`);
  };
  /* 로그인 필요 모달 제어 handler */
  const handleLoginRequireModal = () => {
    setIsOpenLoginModal(!isOpenLoginModal);
  };
  return (
    <div className="h-full">
      <Card
        className={`h-full flex flex-col ${question.isExpired ? 'opacity-75 border-gray-300' : ''} transition-all duration-200 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-1 cursor-pointer group }`}
        onClick={() => handleCardClick(question.questionId)}>
        <CardHeader className="pb-4">
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                {/* 카테고리 */}
                <Badge className="flex items-center gap-1 text-xs h-6 group-hover:bg-primary/10 transition-colors">
                  <span>{categoryDetails && categoryDetails.categoryEmoji}</span>
                  <span>{categoryDetails && categoryDetails.categoryNameKr}</span>
                </Badge>
                {/* 설문 상태 */}
                {question.isExpired ? (
                  <Badge variant="secondary" className="flex items-center gap-1 text-xs bg-gray-100 text-gray-600">
                    <span>🏁</span>
                    <span>종료됨</span>
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className={`flex items-center gap-1 text-xs ${
                      remainingQuestionTime(question.endDate).includes('분') &&
                      !remainingQuestionTime(question.endDate).includes('시간') &&
                      !remainingQuestionTime(question.endDate).includes('일')
                        ? 'border-red-300 text-red-600 bg-red-50'
                        : 'border-green-300 text-green-600 bg-green-50'
                    }`}>
                    <ClockIcon className="w-3 h-3" />
                    <span>{remainingQuestionTime(question.endDate)}</span>
                  </Badge>
                )}
                <div className="w-full">
                  {question.memberOnly && (
                    <Badge variant="outline" className="text-xs bg-red-200 border-orange-200 text-red-500">
                      <span>회원전용</span>
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            {/* 작성자 정보 */}
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-primary/15 transition-colors">
                {question.profileImageUrl ? (
                  <img src={question.profileImageUrl} alt="profile" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <UserIcon className="w-4 h-4 text-primary" />
                )}
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-medium text-fore-ground truncate">{question.username}</span>
                <span className="text-xs text-muted-foreground">{formatTimeAgo(question.startDate)}</span>
              </div>
            </div>
            {/* 설문 제목 */}
            <h3
              className={`leading-relaxed font-medium ${question.isExpired ? 'text-gray-600' : 'text-fore-ground group-hover:text-primary'}`}>
              {question.questionDescription}
            </h3>
          </div>
        </CardHeader>
        <CardContent className="pb-4 flex-1">
          <div className="space-y-4">
            {question.questionExtraContent && (
              <div
                className={`text-sm ${question.isExpired ? 'text-gray-500' : 'text-muted-foreground'} leading-relaxed`}>
                <div className="flex items-start gap-2">
                  <InfoIcon
                    className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0 group-hover:text-primary/70 transition-colors" />
                  <div className="flex-1 min-w-0">
                    <p className="line-clamp-3">{question.questionExtraContent}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
        <CardFooter className="pt-0 flex-col gap-3 mt-auto">
          {/* 설문보기 버튼 */}
          <Button
            variant={question.isExpired ? 'secondary' : 'default'}
            className="w-full h-10 group-hover:shadow-md transition-shadow">
            {question.isExpired ? '설문 결과 보기' : '설문 참여하기'}
          </Button>
          <div
            className="text-sm text-muted-foreground text-right group-hover: text-muted-foreground/80 transition-colors">
            {question.totalVoteCount}명 참여
          </div>
        </CardFooter>
      </Card>
      {/* 상세결과 보기 모달 */}
      {/* 로그인 필요 모달 */}
      <LoginRequireModal isOpenLoginModal={isOpenLoginModal} onCloseLoginRequireModal={handleLoginRequireModal} />
    </div>
  );
};
export default QuestionCardV2;
